
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Volume2, VolumeX, HelpCircle } from 'lucide-react';
import { useSystemAudioCapture } from '@/hooks/useSystemAudioCapture';
import SystemAudioSetupGuide from './SystemAudioSetupGuide';
import { Progress } from '@/components/ui/progress';

interface SystemAudioControlsProps {
  onStreamChange?: (stream: MediaStream | null) => void;
}

const SystemAudioControls: React.FC<SystemAudioControlsProps> = ({
  onStreamChange
}) => {
  const [showGuide, setShowGuide] = useState(false);
  const {
    isCapturing,
    audioLevel,
    audioStream,
    hasVirtualDevice,
    startCapture,
    stopCapture
  } = useSystemAudioCapture();

  React.useEffect(() => {
    onStreamChange?.(audioStream);
  }, [audioStream, onStreamChange]);

  const handleToggle = async () => {
    if (isCapturing) {
      stopCapture();
    } else {
      await startCapture();
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant={isCapturing ? "destructive" : "secondary"}
          size="sm"
          className="gap-2"
          onClick={handleToggle}
        >
          {isCapturing ? (
            <>
              <VolumeX className="h-4 w-4" />
              <span>Stop System Audio</span>
            </>
          ) : (
            <>
              <Volume2 className="h-4 w-4" />
              <span>Share System Audio</span>
            </>
          )}
        </Button>
        
        {isCapturing && (
          <div className="flex items-center gap-2 min-w-[100px]">
            <Progress value={audioLevel * 100} className="h-2" />
          </div>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowGuide(true)}
          title="Setup guide"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </div>

      <SystemAudioSetupGuide
        open={showGuide}
        onOpenChange={setShowGuide}
        onSkip={handleToggle}
      />
    </>
  );
};

export default SystemAudioControls;
